import { useState, useEffect } from 'react'
import { apiService } from '../services/api'
import { Proposal, ProposalFormData } from '../types'

export const useProposals = () => {
 const [proposals, setProposals] = useState<Proposal[]>([])
 const [isLoading, setIsLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const [pagination, setPagination] = useState({
 page: 1,
 limit: 50,
 total: 0,
 totalPages: 0
 })

 const fetchProposals = async (params?: {
 page?: number
 limit?: number
 status?: string
 startDate?: string
 endDate?: string
 validUntilStart?: string
 validUntilEnd?: string
 customerId?: string
 vehicleId?: string
 search?: string
 }) => {
 try {
 setIsLoading(true)
 setError(null)
 const response = await apiService.getProposals(params)
 
 if (response && typeof response === 'object' && 'proposals' in response) {
 setProposals(response.proposals || [])
 if (response.pagination) {
 setPagination(response.pagination)
 }
 } else if (Array.isArray(response)) {
 setProposals(response)
 } else {
 setProposals([])
 }
 } catch (err) {
 console.error('Erro ao carregar propostas:', err)
 setError('Erro ao carregar propostas. Tente novamente.')
 setProposals([])
 } finally {
 setIsLoading(false)
 }
 }

 const createProposal = async (proposalData: ProposalFormData) => {
 try {
 const newProposal = await apiService.createProposal(proposalData)
 if (newProposal) {
 setProposals(prev => [newProposal, ...prev])
 return newProposal
 }
 throw new Error('Proposta criada mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao criar proposta:', err)
 setError('Erro ao criar proposta. Tente novamente.')
 throw err
 }
 }

 const updateProposal = async (id: string, proposalData: Partial<ProposalFormData>) => {
 try {
 const updatedProposal = await apiService.updateProposal(id, proposalData)
 if (updatedProposal) {
 setProposals(prev => prev.map(proposal => proposal.id === id ? updatedProposal : proposal))
 return updatedProposal
 }
 throw new Error('Proposta atualizada mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao atualizar proposta:', err)
 setError('Erro ao atualizar proposta. Tente novamente.')
 throw err
 }
 }

 const deleteProposal = async (id: string) => {
 try {
 await apiService.deleteProposal(id)
 setProposals(prev => prev.filter(proposal => proposal.id !== id))
 } catch (err) {
 console.error('Erro ao deletar proposta:', err)
 setError('Erro ao deletar proposta. Tente novamente.')
 throw err
 }
 }

 const approveProposal = async (id: string, data?: { approvalNotes?: string; approvedAt?: string }) => {
 try {
 const updatedProposal = await apiService.approveProposal(id, data)
 if (updatedProposal) {
 setProposals(prev => prev.map(proposal => proposal.id === id ? updatedProposal : proposal))
 return updatedProposal
 }
 throw new Error('Proposta aprovada mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao aprovar proposta:', err)
 setError('Erro ao aprovar proposta. Tente novamente.')
 throw err
 }
 }

 const rejectProposal = async (id: string) => {
 try {
 const updatedProposal = await apiService.rejectProposal(id)
 if (updatedProposal) {
 setProposals(prev => prev.map(proposal => proposal.id === id ? updatedProposal : proposal))
 return updatedProposal
 }
 throw new Error('Proposta rejeitada mas sem dados retornados')
 } catch (err) {
 console.error('Erro ao rejeitar proposta:', err)
 setError('Erro ao rejeitar proposta. Tente novamente.')
 throw err
 }
 }

 useEffect(() => {
 fetchProposals()
 }, [])

 return {
 proposals,
 isLoading,
 error,
 pagination,
 fetchProposals,
 createProposal,
 updateProposal,
 deleteProposal,
 approveProposal,
 rejectProposal
 }
}
